/**
 * Fluent UI styled Recharts components
 * Professional chart implementations using Recharts with Fluent design system
 */

import React from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { makeStyles, tokens } from '@fluentui/react-components';
import { ChartDataItem, ThemeItem } from '../../../types/forms';
import {
  getFluentChartColors,
  fluentRechartsStyle,
  getColorByTokenPath,
  fluentAnimationConfig,
} from './RechartsFluentTheme';

const useStyles = makeStyles({
  chartContainer: {
    width: '100%',
    marginTop: '16px',
    position: 'relative',
  },
  chartContainerSmall: {
    width: '100%',
    marginTop: '16px',
    position: 'relative',
  },
  // Chart area wrapper with Figma styling
  chartAreaWrapper: {
    backgroundColor: '#F8FAFC',
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: '12px',
    padding: '24px',
  },
  // Figma-style horizontal bar chart
  figmaBarContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  figmaBarRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  figmaBarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  figmaBarLabel: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  figmaBarValue: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '16px',
    textAlign: 'right' as const,
  },
  figmaBarTrack: {
    height: '12px',
    backgroundColor: '#E2E8F0',
    borderRadius: '20px',
    overflow: 'hidden',
    position: 'relative' as const,
  },
  figmaBarFill: {
    height: '100%',
    borderRadius: '20px',
    transition: 'width 0.6s ease-out',
  },
  // PieChart specific container with more bottom space
  pieChartContainer: {
    width: '100%',
    height: '100%',
    minHeight: '200px',
    marginTop: '0px',
    marginBottom: '40px',
    position: 'relative',
  },
  // Theme Cards styles (Category 7)
  themeCardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
    width: '100%',
  },
  themeCard: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  themeCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  themeCardIcon: {
    fontSize: '20px',
  },
  themeCardTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '16px',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  themeCardSummary: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.5',
    margin: 0,
  },
  themeCardQuote: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    borderLeft: `3px solid ${tokens.colorNeutralStroke1}`,
    paddingLeft: '12px',
    margin: 0,
  },
  themeCardCount: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'right' as const,
    marginTop: 'auto',
  },
  // Theme Table styles (Category 7)
  themeTableContainer: {
    width: '100%',
    overflowX: 'auto',
  },
  themeTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  themeTableHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: 'left' as const,
    padding: '12px 16px',
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground1,
  },
  themeTableCell: {
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    verticalAlign: 'top' as const,
    color: tokens.colorNeutralForeground2,
  },
  themeTableTheme: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  themeTableQuote: {
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  themeTableCount: {
    fontWeight: tokens.fontWeightSemibold,
    textAlign: 'center' as const,
  },
  themeTablePercent: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
  },
  // Tag list styles (Category 7 fallback)
  tagListContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  themeTag: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '16px',
    padding: '6px 12px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  themeTagCount: {
    backgroundColor: tokens.colorNeutralBackground4,
    borderRadius: '10px',
    padding: '2px 6px',
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
});

// Custom Fluent UI styled tooltip
const FluentTooltip: React.FC<{ active?: boolean; payload?: Array<{ color: string; value: number; name: string }>; label?: string }> = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div
      style={{
        backgroundColor: fluentRechartsStyle.tooltip.contentStyle.backgroundColor,
        border: fluentRechartsStyle.tooltip.contentStyle.border,
        borderRadius: fluentRechartsStyle.tooltip.contentStyle.borderRadius,
        padding: fluentRechartsStyle.tooltip.contentStyle.padding,
        boxShadow: fluentRechartsStyle.tooltip.contentStyle.boxShadow,
      }}
    >
      <div
        style={{
          color: fluentRechartsStyle.tooltip.labelStyle.color,
          fontWeight: fluentRechartsStyle.tooltip.labelStyle.fontWeight,
          marginBottom: fluentRechartsStyle.tooltip.labelStyle.marginBottom,
        }}
      >
        {label}
      </div>
      {payload.map((entry: { color: string; value: number; name: string; payload?: { percentage?: number } }, index: number) => (
        <div
          key={`item-${index}`}
          style={{
            ...fluentRechartsStyle.tooltip.itemStyle,
            color: entry.color,
          }}
        >
          {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
          {entry.payload?.percentage ? ` (${entry.payload.percentage.toFixed(1)}%)` : ''}
        </div>
      ))}
    </div>
  );
};

/**
 * Grouped Bar Chart - for comparing metrics across categories
 * Each bar uses a different color from the qualitative palette
 * Example: Satisfaction drivers (Productivity 7.39 vs Lite 7.24)
 */
export const FluentGroupedBarChart: React.FC<{
  data: ChartDataItem[];
  dataKeys: string[];
  colors?: string[];
}> = ({ data, dataKeys, colors }) => {
  const chartColors = colors && colors.length > 0 ? colors : getFluentChartColors().categorical;

  // Dynamic bar size based on item count (min 12px)
  const dynamicBarSize = Math.max(12, Math.min(45, Math.floor(280 / data.length) - 10));

  // Calculate dynamic height: tighter spacing for compact display
  const chartHeight = Math.max(200, data.length * 45 + 80);

  return (
    <div style={{ width: '100%', height: `${chartHeight}px`, minHeight: `${chartHeight}px` }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          barCategoryGap="25%"
          margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
        >
        <CartesianGrid {...fluentRechartsStyle.cartesianGrid} />
        <XAxis
          dataKey="label"
          {...fluentRechartsStyle.xAxis}
        />
        <YAxis {...fluentRechartsStyle.yAxis} />
        <Tooltip content={<FluentTooltip />} cursor={{ fill: tokens.colorNeutralBackground3 }} />
        {dataKeys.map((key) => (
          <Bar
            key={key}
            dataKey={key}
            barSize={dynamicBarSize}
            animationDuration={fluentAnimationConfig.duration}
            radius={[4, 4, 0, 0]}
          >
            {data.map((_entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={chartColors[index % chartColors.length]}
              />
            ))}
          </Bar>
        ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Sorted Bar Chart - for preference distribution (horizontal bars)
 * Example: Browser preferences sorted by percentage
 * Bar height fixed at 12px for compact display
 */
export const FluentSortedBarChart: React.FC<{
  data: ChartDataItem[];
}> = ({ data }) => {
  const chartColors = getFluentChartColors().categorical;

  // Sort data by value descending
  const sortedData = [...data].sort((a, b) => (b.value || 0) - (a.value || 0));

  // Fixed bar size of 12px for compact display
  const barSize = 12;

  // Calculate dynamic height: compact spacing
  const chartHeight = Math.max(160, sortedData.length * 32 + 60);

  return (
    <div style={{ width: '100%', height: `${chartHeight}px`, boxSizing: 'border-box' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={sortedData}
          layout="vertical"
          margin={{ top: 5, right: 50, left: 10, bottom: 5 }}
          barCategoryGap="30%"
        >
        <CartesianGrid {...fluentRechartsStyle.cartesianGrid} />
        <XAxis
          type="number"
          domain={[0, 100]}
          tickFormatter={(value) => `${value}%`}
          {...fluentRechartsStyle.xAxis}
        />
        <YAxis
          type="category"
          dataKey="label"
          {...fluentRechartsStyle.yAxis}
          width={90}
        />
        <Tooltip content={<FluentTooltip />} cursor={{ fill: tokens.colorNeutralBackground3 }} />
        <Bar
          dataKey="percentage"
          barSize={barSize}
          animationDuration={fluentAnimationConfig.duration}
          radius={[0, 4, 4, 0]}
          label={{ position: 'right', fill: tokens.colorNeutralForeground1, fontSize: 12 }}
        >
          {sortedData.map((_entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={chartColors[index % chartColors.length]}
            />
          ))}
        </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Ranked Bar Chart - for multi-select rankings (bars relative to max)
 * Example: Feature preferences where percentages exceed 100%
 * Bar height fixed at 12px for compact display
 */
export const FluentRankedBarChart: React.FC<{
  data: ChartDataItem[];
}> = ({ data }) => {
  const chartColors = getFluentChartColors().categorical;

  // Sort data by percentage descending
  const sortedData = [...data].sort((a, b) => (b.percentage || 0) - (a.percentage || 0));

  // Fixed bar size of 12px for compact display
  const barSize = 12;

  // Calculate dynamic height: compact spacing
  const chartHeight = Math.max(160, sortedData.length * 32 + 60);

  return (
    <div style={{ width: '100%', height: `${chartHeight}px`, boxSizing: 'border-box' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={sortedData}
          layout="vertical"
          margin={{ top: 5, right: 50, left: 10, bottom: 5 }}
          barCategoryGap="30%"
        >
        <CartesianGrid {...fluentRechartsStyle.cartesianGrid} />
        <XAxis
          type="number"
          domain={[0, 100]}
          tickFormatter={(value) => `${value}%`}
          {...fluentRechartsStyle.xAxis}
        />
        <YAxis
          type="category"
          dataKey="label"
          {...fluentRechartsStyle.yAxis}
          width={90}
        />
        <Tooltip content={<FluentTooltip />} cursor={{ fill: tokens.colorNeutralBackground3 }} />
        <Bar
          dataKey="percentage"
          barSize={barSize}
          fill={chartColors[0]}
          animationDuration={fluentAnimationConfig.duration}
          radius={[0, 4, 4, 0]}
          label={{ position: 'right', fill: tokens.colorNeutralForeground1, fontSize: 12 }}
        >
          {sortedData.map((_entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={chartColors[index % chartColors.length]} 
            />
          ))}
        </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Stacked Bar Chart - generic stacked bar for comparing segments across categories
 * Example: Preference distribution across demographic segments
 * 🎨 COLOR RULE: Qualitative palette - each data series/segment gets a different color
 */
export const FluentStackedBarChart: React.FC<{
  data: ChartDataItem[];
  dataKeys: string[];
  colors?: string[];
  layout?: 'horizontal' | 'vertical';
  showPercentage?: boolean;
}> = ({ data, dataKeys, colors, layout = 'vertical', showPercentage = true }) => {
  const chartColors = colors && colors.length > 0 ? colors : getFluentChartColors().categorical;

  return (
    <ResponsiveContainer width="100%" height={layout === 'vertical' ? 200 : 380}>
      <BarChart
        data={data}
        layout={layout}
        margin={{ top: 20, right: 20, left: layout === 'vertical' ? 10 : 20, bottom: 20 }}
      >
        {layout === 'vertical' ? (
          <>
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={false}
              axisLine={false}
              tickLine={false}
              hide={true}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={false}
              axisLine={false}
              tickLine={false}
              width={10}
            />
          </>
        ) : (
          <>
            <CartesianGrid {...fluentRechartsStyle.cartesianGrid} />
            <XAxis
              dataKey="label"
              {...fluentRechartsStyle.xAxis}
            />
            <YAxis
              type="number"
              domain={[0, 100]}
              {...fluentRechartsStyle.yAxis}
            />
          </>
        )}
        <Tooltip content={<FluentTooltip />} cursor={{ fill: tokens.colorNeutralBackground3 }} />
        <Legend 
          {...fluentRechartsStyle.legend}
          wrapperStyle={{ marginTop: layout === 'vertical' ? '-10px' : '0px' }}
          formatter={(value) => <span style={{ color: tokens.colorNeutralForeground1 }}>{value}</span>}
        />
        {dataKeys.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            stackId="stack"
            barSize={layout === 'vertical' ? 39 : 45}
            fill={chartColors[index % chartColors.length]}
            animationDuration={fluentAnimationConfig.duration}
            label={showPercentage ? { 
              position: 'top', 
              fill: tokens.colorNeutralForeground1, 
              fontSize: 12, 
              fontWeight: 600, 
              formatter: (value: unknown) => (typeof value === 'number' && value > 5) ? `${value}%` : '' 
            } : undefined}
            radius={index === dataKeys.length - 1 ? (layout === 'vertical' ? [0, 4, 4, 0] : [4, 4, 0, 0]) : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Sentiment Bar Chart - stacked bar showing positive/neutral/negative
 * Example: User experience strengths and pain points
 * 🎨 COLOR RULE: Semantic gradient colors (green/gray/red) with 4px radius on each section
 */
export const FluentSentimentBarChart: React.FC<{
  data: { positive: number; neutral: number; negative: number };
  label: string;
}> = ({ data }) => {
  // Normalize data to ensure it sums to 100 for full-width display
  const total = data.positive + data.neutral + data.negative;
  const normalizedData = total > 0 ? {
    positive: Math.round((data.positive / total) * 100),
    neutral: Math.round((data.neutral / total) * 100),
    negative: Math.round((data.negative / total) * 100),
  } : data;

  // Adjust for rounding errors to ensure exactly 100%
  const sum = normalizedData.positive + normalizedData.neutral + normalizedData.negative;
  if (sum !== 100 && sum > 0) {
    // Add difference to the largest value
    const max = Math.max(normalizedData.positive, normalizedData.neutral, normalizedData.negative);
    if (normalizedData.positive === max) {
      normalizedData.positive += (100 - sum);
    } else if (normalizedData.neutral === max) {
      normalizedData.neutral += (100 - sum);
    } else {
      normalizedData.negative += (100 - sum);
    }
  }

  console.log('FluentSentimentBarChart - original data:', data);
  console.log('FluentSentimentBarChart - normalized data:', normalizedData);

  // Figma-style gradient colors
  const gradients = {
    positive: 'linear-gradient(90deg, #13a10e 0%, #187014 100%)',
    neutral: 'linear-gradient(90deg, #cbd5e1 0%, #a5acb5 100%)',
    negative: 'linear-gradient(90deg, #f24e1e 0%, #b03535 100%)',
  };

  // Legend colors (solid for display)
  const legendColors = {
    positive: '#16a34a',
    neutral: '#bdbdbd',
    negative: '#c2410c',
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Stacked Bar with CSS-based gradients and individual border-radius */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '4px',
        width: '100%',
        height: '20px',
      }}>
        {normalizedData.positive > 0 && (
          <div
            style={{
              flex: normalizedData.positive,
              height: '100%',
              background: gradients.positive,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: '6px',
              overflow: 'hidden',
            }}
          >
            {normalizedData.positive > 5 && (
              <span style={{ 
                color: 'white', 
                fontSize: '12px', 
                fontWeight: 600,
              }}>
                {normalizedData.positive}
              </span>
            )}
          </div>
        )}
        {normalizedData.neutral > 0 && (
          <div
            style={{
              flex: normalizedData.neutral,
              height: '100%',
              background: gradients.neutral,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: '6px',
              overflow: 'hidden',
            }}
          >
            {normalizedData.neutral > 5 && (
              <span style={{ 
                color: 'white', 
                fontSize: '12px', 
                fontWeight: 600,
              }}>
                {normalizedData.neutral}
              </span>
            )}
          </div>
        )}
        {normalizedData.negative > 0 && (
          <div
            style={{
              flex: normalizedData.negative,
              height: '100%',
              background: gradients.negative,
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: '6px',
              overflow: 'hidden',
            }}
          >
            {normalizedData.negative > 5 && (
              <span style={{ 
                color: 'white', 
                fontSize: '12px', 
                fontWeight: 600,
              }}>
                {normalizedData.negative}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Legend: Positive | Neutral | Negative */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        gap: '24px',
        marginTop: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ 
            width: '16px', 
            height: '16px', 
            backgroundColor: legendColors.positive, 
            borderRadius: '4px' 
          }} />
          <span style={{ 
            fontSize: '14px', 
            fontWeight: 600, 
            color: tokens.colorNeutralForeground1 
          }}>
            Positive
          </span>
          <span style={{ 
            fontSize: '14px', 
            fontWeight: 400, 
            color: tokens.colorNeutralForeground1 
          }}>
            {normalizedData.positive}%
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ 
            width: '16px', 
            height: '16px', 
            backgroundColor: legendColors.neutral, 
            borderRadius: '4px' 
          }} />
          <span style={{ 
            fontSize: '14px', 
            fontWeight: 600, 
            color: tokens.colorNeutralForeground1 
          }}>
            Neutral
          </span>
          <span style={{ 
            fontSize: '14px', 
            fontWeight: 400, 
            color: tokens.colorNeutralForeground1 
          }}>
            {normalizedData.neutral}%
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ 
            width: '16px', 
            height: '16px', 
            backgroundColor: legendColors.negative, 
            borderRadius: '4px' 
          }} />
          <span style={{ 
            fontSize: '14px', 
            fontWeight: 600, 
            color: tokens.colorNeutralForeground1 
          }}>
            Negative
          </span>
          <span style={{ 
            fontSize: '14px', 
            fontWeight: 400, 
            color: tokens.colorNeutralForeground1 
          }}>
            {normalizedData.negative}%
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Pie Chart - for distribution visualization
 * Example: Response distribution across categories
 * isDonut: When true, renders as a donut chart (good for 2 options like Yes/No)
 * showLegend: When true, shows a legend below the chart instead of labels
 */
export const FluentPieChart: React.FC<{
  data: ChartDataItem[];
  isDonut?: boolean;
  showLabels?: boolean;
  showLegend?: boolean;
  width?: number | string;
  height?: number | string;
}> = ({ data, isDonut = false, showLabels = true, showLegend = false, width, height }) => {
  const styles = useStyles();
  const chartColors = getFluentChartColors().categorical;

  const pieData = data.map((item, index) => ({
    name: item.label,
    value: item.value || item.percentage || 0,
    color: item.color ? getColorByTokenPath(item.color) : chartColors[index % chartColors.length],
  }));

  // Calculate total for percentage display
  const total = pieData.reduce((sum, item) => sum + item.value, 0);

  // Custom label with line pointing to slice
  const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, name, value }: {
    cx: number; cy: number; midAngle: number; outerRadius: number; name: string; value: number;
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const percentage = ((value / total) * 100).toFixed(0);
    
    return (
      <text
        x={x}
        y={y}
        fill={tokens.colorNeutralForeground1}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '12px' }}
      >
        {`${name}: ${percentage}%`}
      </text>
    );
  };

  // Adjust size based on whether legend is shown
  const outerRadius = showLegend ? (isDonut ? 100 : 120) : (isDonut ? 132 : 154);
  const innerRadiusValue = showLegend ? (isDonut ? 60 : 0) : (isDonut ? 79 : 0);

  // Custom legend formatter to show percentage
  const renderLegendText = (value: string) => {
    const item = pieData.find(d => d.name === value);
    const percentage = item ? ((item.value / total) * 100).toFixed(0) : 0;
    return `${value}: ${percentage}%`;
  };

  // Use explicit dimensions when provided to avoid ResponsiveContainer issues in flex layouts
  const containerStyle = width || height ? {
    width: width || '100%',
    height: height || '200px',
    minHeight: height || '200px',
  } : {};
  
  return (
    <div className={styles.pieChartContainer} style={containerStyle}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 10, right: 10, bottom: showLegend ? 40 : 10, left: 10 }}>
          <Pie
            data={pieData}
            cx="50%"
            cy={showLegend ? "40%" : "45%"}
            labelLine={showLabels && !showLegend}
            label={showLabels && !showLegend ? renderCustomLabel as (props: unknown) => React.ReactElement : undefined}
            outerRadius={outerRadius}
            innerRadius={innerRadiusValue}
            fill="#8884d8"
            dataKey="value"
            animationDuration={fluentAnimationConfig.duration}
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<FluentTooltip />} />
          {showLegend && (
            <Legend 
              verticalAlign="bottom" 
              height={36}
              formatter={renderLegendText}
              wrapperStyle={{ paddingTop: '20px' }}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Line Chart - for trend visualization
 * Example: Satisfaction trends over time
 */
export const FluentLineChart: React.FC<{
  data: ChartDataItem[];
  dataKeys: string[];
  colors?: string[];
}> = ({ data, dataKeys, colors }) => {
  const styles = useStyles();
  const chartColors = colors || getFluentChartColors().categorical;

  // Use fixed height to avoid ResponsiveContainer issues in flex layouts
  return (
    <div className={styles.chartContainer}>
      <ResponsiveContainer width="100%" height={380}>
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid {...fluentRechartsStyle.cartesianGrid} />
          <XAxis
            dataKey="label"
            {...fluentRechartsStyle.xAxis}
          />
          <YAxis {...fluentRechartsStyle.yAxis} />
          <Tooltip content={<FluentTooltip />} cursor={{ fill: tokens.colorNeutralBackground3 }} />
          <Legend {...fluentRechartsStyle.legend} />
          {dataKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={chartColors[index % chartColors.length]}
              strokeWidth={2}
              dot={{ r: 4 }}
              animationDuration={fluentAnimationConfig.duration}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Radar Chart - for multi-dimensional comparison
 * Shows multiple metrics/attributes for comparison across categories
 * Example: Product attributes comparison, skill assessments
 * 🎨 COLOR RULE: Qualitative palette - each data series gets a different color
 */
export const FluentRadarChart: React.FC<{
  data: ChartDataItem[];
  dataKeys?: string[];
  colors?: string[];
  showLegend?: boolean;
}> = ({ data, dataKeys = ['value'], colors, showLegend = true }) => {
  const styles = useStyles();
  const chartColors = colors && colors.length > 0 
    ? colors.map(c => c.startsWith('qualitative') || c.startsWith('sequential') || c.startsWith('semantic')
        ? getColorByTokenPath(c)
        : c)
    : getFluentChartColors().categorical;

  // Use fixed height to avoid ResponsiveContainer issues in flex layouts
  return (
    <div className={styles.chartContainer}>
      <ResponsiveContainer width="100%" height={380}>
        <RadarChart
          data={data}
          margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
        >
          <PolarGrid 
            stroke="#e0e0e0"
            strokeDasharray="3 3"
          />
          <PolarAngleAxis 
            dataKey="label"
            tick={{ 
              fill: '#616161', 
              fontSize: 12,
              fontFamily: 'Segoe UI, sans-serif'
            }}
          />
          <PolarRadiusAxis 
            angle={90}
            domain={[0, 'auto']}
            tick={{ 
              fill: '#9e9e9e', 
              fontSize: 10 
            }}
          />
          {dataKeys.map((key, index) => (
            <Radar
              key={key}
              name={key === 'value' ? 'Score' : key}
              dataKey={key}
              stroke={chartColors[index % chartColors.length]}
              fill={chartColors[index % chartColors.length]}
              fillOpacity={0.3}
              strokeWidth={2}
              animationDuration={fluentAnimationConfig.duration}
            />
          ))}
          <Tooltip content={<FluentTooltip />} cursor={{ fill: tokens.colorNeutralBackground3 }} />
          {showLegend && dataKeys.length > 1 && (
            <Legend 
              {...fluentRechartsStyle.legend}
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{ 
                paddingTop: '8px',
                fontSize: '12px',
              }}
            />
          )}
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ============================================================================
// Figma-Style Horizontal Bar Chart (CSS-based, not Recharts)
// Label + Value header above each bar, with colored fill
// ============================================================================

interface FigmaBarItem {
  label: string;
  value: number;
  color?: string;
}

interface FigmaHorizontalBarChartProps {
  data: FigmaBarItem[];
  maxValue?: number;
  showPercentage?: boolean;
  colors?: string[];
  valueFormatter?: (value: number) => string;
  /** If true, renders without the chartAreaWrapper (use when already inside a styled container) */
  noWrapper?: boolean;
}

export const FigmaHorizontalBarChart: React.FC<FigmaHorizontalBarChartProps> = ({
  data,
  maxValue,
  showPercentage = false,
  colors,
  valueFormatter,
  noWrapper = false,
}) => {
  const styles = useStyles();
  
  // Calculate max value for percentage width
  const calculatedMax = maxValue ?? Math.max(...data.map(d => d.value), 1);
  
  // Get chart colors
  const chartColors = colors && colors.length > 0 
    ? colors.map(c => c.startsWith('qualitative') || c.startsWith('sequential') || c.startsWith('semantic')
        ? getColorByTokenPath(c)
        : c)
    : getFluentChartColors().categorical;

  const formatValue = (value: number): string => {
    if (valueFormatter) return valueFormatter(value);
    if (showPercentage) return `${value}%`;
    return value.toLocaleString();
  };

  const barContent = (
    <div className={styles.figmaBarContainer}>
      {data.map((item, index) => {
        const barColor = item.color || chartColors[index % chartColors.length];
        const widthPercent = (item.value / calculatedMax) * 100;
        
        return (
          <div key={`${item.label}-${index}`} className={styles.figmaBarRow}>
            <div className={styles.figmaBarHeader}>
              <span className={styles.figmaBarLabel}>{item.label}</span>
              <span className={styles.figmaBarValue} style={{ color: barColor }}>
                {formatValue(item.value)}
              </span>
            </div>
            <div className={styles.figmaBarTrack}>
              <div 
                className={styles.figmaBarFill} 
                style={{ 
                  width: `${Math.min(widthPercent, 100)}%`,
                  backgroundColor: barColor,
                }} 
              />
            </div>
          </div>
        );
      })}
    </div>
  );

  if (noWrapper) {
    return barContent;
  }

  return (
    <div className={styles.chartAreaWrapper}>
      {barContent}
    </div>
  );
};

// =============================================================================
// Category 7: Open-Text Theme Components
// =============================================================================

/**
 * Theme Cards Component
 * Primary display for Category 7 when themes ≤ 6
 * Shows themes in a card grid with summary and representative quote
 */
export interface FluentThemeCardsProps {
  themes: ThemeItem[];
  /** Total response count for percentage calculation */
  totalResponses?: number;
}

export const FluentThemeCards: React.FC<FluentThemeCardsProps> = ({
  themes,
  totalResponses,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.themeCardsContainer}>
      {themes.map((theme, index) => {
        const percentage = totalResponses 
          ? Math.round(((theme.count || 0) / totalResponses) * 100) 
          : theme.percentage || 0;
        
        return (
          <div key={`theme-card-${index}`} className={styles.themeCard}>
            <div className={styles.themeCardHeader}>
              <span className={styles.themeCardIcon}>🏷️</span>
              <h4 className={styles.themeCardTitle}>{theme.theme || theme.name}</h4>
            </div>
            <p className={styles.themeCardSummary}>{theme.summary}</p>
            <p className={styles.themeCardQuote}>"{theme.quote}"</p>
            <span className={styles.themeCardCount}>
              {theme.count || 0} {(theme.count || 0) === 1 ? 'response' : 'responses'}
              {percentage !== undefined && ` (${percentage}%)`}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Theme Table Component
 * Secondary display for Category 7 when themes > 6
 * Shows themes in a structured table format
 */
export interface FluentThemeTableProps {
  themes: ThemeItem[];
  /** Total response count for percentage calculation */
  totalResponses?: number;
  /** Whether to show the percentage column */
  showPercentage?: boolean;
}

export const FluentThemeTable: React.FC<FluentThemeTableProps> = ({
  themes,
  totalResponses,
  showPercentage = true,
}) => {
  const styles = useStyles();

  // Sort themes by count descending
  const sortedThemes = [...themes].sort((a, b) => (b.count || 0) - (a.count || 0));

  return (
    <div className={styles.themeTableContainer}>
      <table className={styles.themeTable}>
        <thead>
          <tr>
            <th className={styles.themeTableHeader}>Theme</th>
            <th className={styles.themeTableHeader} style={{ width: '80px', textAlign: 'center' }}>Count</th>
            {showPercentage && (
              <th className={styles.themeTableHeader} style={{ width: '80px', textAlign: 'center' }}>%</th>
            )}
            <th className={styles.themeTableHeader}>Sample Quote</th>
          </tr>
        </thead>
        <tbody>
          {sortedThemes.map((theme, index) => {
            const percentage = totalResponses 
                ? Math.round(((theme.count || 0) / totalResponses) * 100)
                : theme.percentage || 0;
            return (
              <tr key={`theme-row-${index}`}>
                <td className={`${styles.themeTableCell} ${styles.themeTableTheme}`}>
                  {theme.theme || theme.name}
                </td>
                <td className={`${styles.themeTableCell} ${styles.themeTableCount}`}>
                  {theme.count || 0}
                </td>
                {showPercentage && (
                  <td className={`${styles.themeTableCell} ${styles.themeTablePercent}`}>
                    {percentage !== undefined ? `${percentage}%` : '—'}
                  </td>
                )}
                <td className={`${styles.themeTableCell} ${styles.themeTableQuote}`}>
                  "{theme.quote || ''}"
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {totalResponses && (
        <div style={{ 
          marginTop: '12px', 
          fontSize: '13px', 
          color: tokens.colorNeutralForeground3,
          textAlign: 'right' 
        }}>
          Total responses with themes: {totalResponses}
        </div>
      )}
    </div>
  );
};

/**
 * Theme Tag List Component
 * Fallback display for Category 7 - most compact representation
 */
export interface FluentThemeTagListProps {
  themes: ThemeItem[];
}

export const FluentThemeTagList: React.FC<FluentThemeTagListProps> = ({
  themes,
}) => {
  const styles = useStyles();

  // Sort themes by count descending
  const sortedThemes = [...themes].sort((a, b) => (b.count || 0) - (a.count || 0));

  return (
    <div className={styles.tagListContainer}>
      {sortedThemes.map((theme, index) => (
        <span key={`theme-tag-${index}`} className={styles.themeTag}>
          {theme.theme || theme.name}
          <span className={styles.themeTagCount}>×{theme.count || 0}</span>
        </span>
      ))}
    </div>
  );
};
